import { useEffect, useState } from "react";
import client from "../lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, Sparkle, Copy, ListChecks, EnvelopeSimple } from "@phosphor-icons/react";

export const LeadReplyTools = ({ lead, onUpdate }) => {
  const [templates, setTemplates] = useState([]);
  const [activeTpl, setActiveTpl] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [checklistSending, setChecklistSending] = useState(false);

  useEffect(() => {
    setActiveTpl(null);
    setSubject("");
    setBody("");
    client
      .get(`/admin/contact-requests/${lead.id}/reply-templates`)
      .then((r) => setTemplates(r.data.templates || []))
      .catch(() => setTemplates([]));
  }, [lead.id]);

  const pickTemplate = (t) => {
    setActiveTpl(t.id);
    setSubject(t.subject);
    setBody(t.body);
  };

  const aiDraft = async () => {
    setAiLoading(true);
    try {
      const { data } = await client.post(`/admin/contact-requests/${lead.id}/ai-draft`);
      setActiveTpl("ai");
      setSubject(data.subject);
      setBody(data.body);
      toast.success("AI draft ready — edit before sending");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed");
    } finally {
      setAiLoading(false);
    }
  };

  const sendReply = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Pick a template or draft first");
    setSending(true);
    try {
      const { data } = await client.post(`/admin/contact-requests/${lead.id}/send-reply`, {
        subject,
        body,
        template_id: activeTpl || "custom",
      });
      toast.success(`Reply sent to ${lead.email}`);
      onUpdate?.({ manual_reply_sent_at: data.manual_reply_sent_at, ...(data.status ? { status: data.status } : {}) });
      setActiveTpl(null);
      setSubject("");
      setBody("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(body);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const sendChecklist = async () => {
    setChecklistSending(true);
    try {
      const { data } = await client.post(`/admin/contact-requests/${lead.id}/send-onboarding-checklist`);
      toast.success("Onboarding checklist sent");
      onUpdate?.({ onboarding_checklist_sent_at: data.onboarding_checklist_sent_at });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setChecklistSending(false);
    }
  };

  const emailBlocked = lead.email_trusted === false;

  return (
    <div className="mb-6" data-testid="lead-reply-tools">
      <div className="overline mb-2">QUICK REPLY</div>

      <div className="flex flex-wrap gap-2 mb-3">
        {templates.map((t) => (
          <button
            key={t.id}
            data-testid={`reply-template-${t.id}`}
            onClick={() => pickTemplate(t)}
            className={`rounded-full px-3.5 py-1.5 text-xs border transition-colors ${
              activeTpl === t.id
                ? "border-[#FF5A1F] bg-[#FF5A1F]/15 text-[#FF9764]"
                : "border-white/15 text-neutral-300 hover:border-white/30 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
        <button
          data-testid="reply-ai-draft-btn"
          onClick={aiDraft}
          disabled={aiLoading}
          className={`rounded-full px-3.5 py-1.5 text-xs border inline-flex items-center gap-1.5 transition-colors ${
            activeTpl === "ai"
              ? "border-violet-500 bg-violet-500/15 text-violet-300"
              : "border-violet-500/40 text-violet-300 hover:border-violet-400"
          } ${aiLoading ? "opacity-60 cursor-wait" : ""}`}
        >
          <Sparkle size={12} weight="fill" />
          {aiLoading ? "Drafting..." : "AI draft"}
        </button>
      </div>

      {(subject || body) && (
        <div className="rounded-md bg-[#0F0F0F] border border-white/10 p-4 space-y-3" data-testid="reply-draft-panel">
          <input
            data-testid="reply-draft-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF5A1F]/60"
            placeholder="Subject"
          />
          <textarea
            data-testid="reply-draft-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={9}
            className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-neutral-200 leading-relaxed focus:outline-none focus:border-[#FF5A1F]/60 resize-y"
          />
          <div className="flex flex-wrap gap-2">
            {emailBlocked ? (
              <span
                data-testid="reply-send-blocked"
                className="rounded-full px-4 py-2 text-xs border border-amber-500/30 text-amber-300 inline-flex items-center gap-1.5"
              >
                Email flagged as fake — copy the text and use the phone instead
              </span>
            ) : (
              <button
                data-testid="reply-send-btn"
                onClick={sendReply}
                disabled={sending}
                className={`btn-primary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5 ${sending ? "opacity-60 cursor-wait" : ""}`}
              >
                <PaperPlaneTilt size={12} weight="bold" />
                {sending ? "Sending..." : `Send to ${lead.email}`}
              </button>
            )}
            <button
              data-testid="reply-copy-btn"
              onClick={copyDraft}
              className="rounded-full px-4 py-2 text-xs border border-white/15 text-neutral-300 hover:border-white/30 hover:text-white inline-flex items-center gap-1.5 transition-colors"
            >
              <Copy size={12} weight="bold" /> Copy
            </button>
            {!emailBlocked && (
              <a
                data-testid="reply-mailto-btn"
                href={`mailto:${lead.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}
                className="rounded-full px-4 py-2 text-xs border border-white/15 text-neutral-300 hover:border-white/30 hover:text-white inline-flex items-center gap-1.5 transition-colors"
              >
                <EnvelopeSimple size={12} weight="bold" /> Open in mail app
              </a>
            )}
          </div>
        </div>
      )}

      {lead.manual_reply_sent_at && (
        <div data-testid="reply-sent-stamp" className="mt-2 text-[11px] text-neutral-500 font-mono">
          Reply sent {new Date(lead.manual_reply_sent_at).toLocaleString()}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-white/5">
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="send-onboarding-checklist-btn"
            onClick={sendChecklist}
            disabled={checklistSending || emailBlocked}
            title={emailBlocked ? "Email flagged as fake — checklist would bounce" : "Email the 6-step unpaid-loads demo prep checklist"}
            className={`rounded-full px-4 py-2 text-xs border inline-flex items-center gap-1.5 transition-colors ${
              emailBlocked
                ? "border-white/10 text-neutral-600 cursor-not-allowed"
                : "border-emerald-500/40 text-emerald-300 hover:border-emerald-400"
            } ${checklistSending ? "opacity-60 cursor-wait" : ""}`}
          >
            <ListChecks size={13} weight="bold" />
            {checklistSending ? "Sending..." : "Send Onboarding Checklist"}
          </button>
          <span className="text-[11px] text-neutral-500">
            Walks them through adding unpaid loads in Invoices before their demo.
          </span>
        </div>
        {lead.onboarding_checklist_sent_at && (
          <div data-testid="checklist-sent-stamp" className="mt-2 text-[11px] text-emerald-400/70 font-mono">
            Checklist sent {new Date(lead.onboarding_checklist_sent_at).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};
